import { getMyBookingLists } from 'apis/booking';
import { postMembershipSets } from 'apis/membership';
import { MyBookingCardList } from 'components/my/MyBookingCardList';
import { MyMembershipCardList } from 'components/my/MyMembershipCardList';
import { BaseButton } from 'components/ui/BaseButton';
import { BaseLine } from 'components/ui/BaseLine';
import { S3_BASE_URL, STATIC_URL } from 'constants/static';
import { FooterLayout } from 'layouts/FooterLayout';
import { EventMainPage } from 'components/event/EventMainPage';
import { useTranslation } from 'i18n';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  isAuthenticatedState,
  isLoginFormOpenState,
  loadingState,
  shopDetailState,
  shopState,
} from 'store/atoms';
import {
  MainContainer,
  Content,
  GolfTitle,
  GolfInfo,
  InfoItem,
  ButtonGroup,
  SectionContainer,
  MyPageContainer,
  SectionHeader,
  SectionTitle,
  ArrowButton,
  NoDataText,
  SignInTitle,
  NoDataCard,
  SignInButtonWrapper,
  NoDataButtonContainer,
} from './MainPage.styles';

export { NoDataText, NoDataCard } from './MainPage.styles';

export default function MainPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const shop = useRecoilValue(shopState);
  const shopDetail = useRecoilValue(shopDetailState);

  const isAuthenticated = useRecoilValue(isAuthenticatedState);
  const setIsLoginFormOpen = useSetRecoilState(isLoginFormOpenState);
  const loading = useRecoilValue(loadingState);

  const isPaymentUsed = shopDetail?.solutionConfig?.global_config?.isGeneralPaymentUsed;

  function sendToMiniProgram(action: string, data?: any) {
    window.wx?.miniProgram.navigateTo({
      url: `/pages/bridge/scan?cbKey=kcd_scan_result`,
    });
  }

  function handleScan() {
    sendToMiniProgram('scanQr');
  }

  const handleBookTeeTime = () => {
    navigate('/booking');
  };

  const handleBuyMembership = () => {
    navigate('/membership');
  };

  const handleMyBookingClick = () => {
    navigate('/my/booking');
  };

  const handleMyMembershipClick = () => {
    navigate('/my/membership');
  };

  const goToLoginPage = () => {
    setIsLoginFormOpen(true);
  };

  const handleBookingDetailClick = (bookingId: number) => {
    navigate(`/my/booking/${bookingId}`);
  };

  const handleMembershipDetailClick = (membershipId: number) => {
    navigate(`/my/membership/${membershipId}`);
  };

  const [bookingLists, setBookingLists] = useState<any>([]);
  const [membershipLists, setMembershipLists] = useState<any>([]);

  const getMyBookingList = async () => {
    const res: any = await getMyBookingLists(shop?.shopSeqNo || 0);
    setBookingLists(res?.data.items);
  };

  const getMyMembershipList = async () => {
    const res: any = await postMembershipSets({
      shopSeqNo: shop?.shopSeqNo,
    });
    setMembershipLists(res);
  };

  useEffect(() => {
    if (loading) return;

    const token = localStorage.getItem('customer_token');
    if (!token) return;
    if (!shop?.shopSeqNo) return;

    getMyBookingList();
    getMyMembershipList();
  }, [loading, isAuthenticated, shop?.shopSeqNo]);

  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const shopParam = urlParams.get('shop');
  const shopIdParam = urlParams.get('shopId');

  if (!shopParam && !shopIdParam && !shop) {
    window.location.href = 'https://www.kaddie.golf';
    return null;
  } else {
    return (
      <>
        <MainContainer $backgroundImage={`${STATIC_URL}/${shop?.shopImageTitle}`}>
          <Content>
            <GolfTitle onClick={handleScan}>{shop?.shopName}</GolfTitle>

            <GolfInfo>
              <InfoItem>
                <img src={`${S3_BASE_URL}/address_icon.svg`} alt="location" />
                {shop?.shopAddress}
              </InfoItem>
              <InfoItem>
                <img src={`${S3_BASE_URL}/tel_icon.svg`} alt="phone" />
                {shop?.shopPhone}
              </InfoItem>
              <InfoItem>
                <img src={`${S3_BASE_URL}/email_icon.svg`} alt="email" />
                {shop?.shopEmail}
              </InfoItem>
            </GolfInfo>

            <ButtonGroup>
              <BaseButton
                text={t('home.cta.book_tee_time')}
                onClick={handleBookTeeTime}
                variant="primary"
                size="large"
              />
              <BaseButton
                text={isPaymentUsed ? t('home.cta.buy_membership') : t('home.cta.view_membership')}
                onClick={handleBuyMembership}
                variant="primary"
                size="large"
              />
            </ButtonGroup>
          </Content>
        </MainContainer>

        <MyPageContainer>
          {/* 로그인 X */}
          {!isAuthenticated && (
            <SectionContainer>
              <SignInTitle>{t('home.signin.title')}</SignInTitle>
              <NoDataCard>
                <NoDataText>{t('home.signin.description')}</NoDataText>
                <SignInButtonWrapper>
                  <BaseButton text={t('home.signin.button')} onClick={goToLoginPage} variant="green" size="small" />
                </SignInButtonWrapper>
              </NoDataCard>
            </SectionContainer>
          )}

          {/* 로그인 O / DATA */}
          {isAuthenticated && (
            <SectionContainer>
              <SectionHeader>
                <SectionTitle>{t('my_booking.title')}</SectionTitle>
                <ArrowButton onClick={handleMyBookingClick}>
                  <img src={`${S3_BASE_URL}/black_arrow_icon.svg`} alt="black arrow icon" />
                </ArrowButton>
              </SectionHeader>
              {bookingLists?.length > 0 ? (
                <MyBookingCardList
                  bookings={bookingLists}
                  onBookingClick={(id) => handleBookingDetailClick(id)}
                  showSingleCard={true}
                />
              ) : (
                <NoDataCard>
                  <NoDataText>{t('my_booking.no_data')}</NoDataText>
                  <NoDataButtonContainer>
                    <BaseButton
                      text={t('my_booking.cta.book_tee_time')}
                      onClick={handleBookTeeTime}
                      variant="green"
                      size="small"
                    />
                  </NoDataButtonContainer>
                </NoDataCard>
              )}

              <BaseLine margin="26px 0" />
              <SectionHeader>
                <SectionTitle>{t('my_membership.title')}</SectionTitle>
                <ArrowButton onClick={handleMyMembershipClick}>
                  <img src={`${S3_BASE_URL}/black_arrow_icon.svg`} alt="black arrow icon" />
                </ArrowButton>
              </SectionHeader>
              {membershipLists?.length > 0 ? (
                <MyMembershipCardList
                  memberships={membershipLists}
                  showSingleCard={true}
                  onCardClick={(id) => handleMembershipDetailClick(id)}
                />
              ) : (
                <NoDataCard>
                  <NoDataText>{t('my_membership.no_data')}</NoDataText>
                  <NoDataButtonContainer>
                    <BaseButton
                      text={t('buy_membership.cta.become_member')}
                      onClick={handleBuyMembership}
                      variant="green"
                      size="small"
                    />
                  </NoDataButtonContainer>
                </NoDataCard>
              )}
            </SectionContainer>
          )}
        </MyPageContainer>

        <EventMainPage />
        <FooterLayout />
      </>
    );
  }
}
